import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { PaginatedList } from "libs/models/paginated-list";
import { EqualFilter, Filter } from "libs/models/filter";
import { FilterTab } from "components/tab-filter/tab-filter.props";
import { AsyncAppThunk } from "store/application-state";
import { addSnack, showLoaderWhile } from "./ui";
import { getFilterQuery } from "utils/filter";
import {
  addProductService,
  editProductService,
  getClientService,
  getProductDetailsByIdService,
  getProductsService,
} from "services/crosspay-service";
import {
  ProductModel,
  ProductRedemptionClients,
  ProductRequestModel,
  UpdateProductRequestModel,
} from "libs/models/products";
import { validateAddProductState } from "pages/AddProduct/add-product.policy";
import { ClientModel } from "libs/models/client";
import { CustomizedBack } from "store/navigation-wrapper";
import { validateEditProductState } from "pages/EditProduct/edit-product.policy";
import { productDetailsFilterTabs } from "pages/ProductDetailsView/products-details.util";
import translate from "utils/translations";

export interface ProductsStore {
  isValidating: boolean;
  newProductData: ProductRequestModel;
  updatedProductData: ProductModel;
  productsData: PaginatedList<ProductModel>;
  productDetails: ProductModel;
  productFilter: Filter;
  clientFilter: Filter;
  searchingValue: string | undefined;
  productItemsSearchValue: string | undefined;
  tabFilterValue: FilterTab;
  clients: PaginatedList<ClientModel>;
  clientSearchValue: string | undefined;
  productRedemptionClients: ProductRedemptionClients[];
}

const initialState: ProductsStore = {
  isValidating: false,
  updatedProductData: {} as ProductModel,
  productDetails: {} as ProductModel,
  newProductData: {} as ProductRequestModel,
  productsData: { list: [], pageSize: 10, pageIndex: 0, totalCount: 0 },
  productFilter: {
    orderBy: { key: "Name", sort: "asc" },
  },

  searchingValue: undefined,
  productItemsSearchValue: undefined,
  tabFilterValue: productDetailsFilterTabs[0],
  clients: { list: [], pageSize: 10, pageIndex: 0, totalCount: 0 },
  clientSearchValue: undefined,
  clientFilter: {
    orderBy: { key: "Name", sort: "asc" },
  },
  productRedemptionClients: [],
};

const slice = createSlice({
  name: "products",
  initialState,
  reducers: {
    setProductsFetchingData: (
      state: ProductsStore,
      action: PayloadAction<PaginatedList<ProductModel>>,
    ) => {
      state.productsData = action.payload;
    },

    setClientsFetchingData: (
      state: ProductsStore,
      action: PayloadAction<PaginatedList<ClientModel>>,
    ) => {
      state.clients = action.payload;
    },
    setUpdatedClientsFetchingData: (
      state: ProductsStore,
      action: PayloadAction<PaginatedList<ClientModel>>,
    ) => {
      const redemptionClients = [...action.payload.list];
      for (let cIndex = 0; cIndex < redemptionClients.length; cIndex++) {
        const client = redemptionClients[cIndex];
        for (
          let rcIndexindex = 0;
          rcIndexindex <
          state.updatedProductData.productRedemptionClients?.length;
          rcIndexindex++
        ) {
          const redemptionClient =
            state.updatedProductData.productRedemptionClients[rcIndexindex];
          if (
            client.id === redemptionClient.clientId ||
            client.name === redemptionClient.clientName
          ) {
            redemptionClients[cIndex].disabled =
              redemptionClient.marginFees !== undefined;
            redemptionClients[cIndex].marginFees = redemptionClient.marginFees;
            redemptionClients[cIndex].isMarginFeesPercentage =
              redemptionClient.isMarginFeesPercentage;
          }
        }
      }
      state.clients = {
        list: redemptionClients,
        pageSize: action.payload.pageSize,
        pageIndex: action.payload.pageIndex,
        totalCount: action.payload.totalCount,
      };
    },
    setIsValidating: (state: ProductsStore, action: PayloadAction<boolean>) => {
      state.isValidating = action.payload;
    },
    setEqualFilter: (
      state: ProductsStore,
      action: PayloadAction<{ key: string; value?: any }>,
    ) => {
      const { key, value } = action.payload;
      if (value === null) {
        delete state.productFilter[key];
        return state;
      } else {
        state.productFilter = { ...state.productFilter, [key]: value! };
      }
      return state;
    },
    setProductViewDynamicSearchFilter: (
      state: ProductsStore,
      action: PayloadAction<EqualFilter[]>,
    ) => {
      state.productFilter = { ...state.productFilter, or: action.payload };
    },
    setSearchKey: (state: ProductsStore, action: PayloadAction<string>) => {
      state.searchingValue = action.payload;
    },
    setProductItemsSearchKey: (
      state: ProductsStore,
      action: PayloadAction<string>,
    ) => {
      state.productItemsSearchValue = action.payload;
    },
    setClientSearchKey: (
      state: ProductsStore,
      action: PayloadAction<string>,
    ) => {
      state.clientSearchValue = action.payload;
    },
    setTabFilterChange: (
      state: ProductsStore,
      action: PayloadAction<FilterTab>,
    ) => {
      state.tabFilterValue = action.payload;
    },

    setProductsPage: (state: ProductsStore, action: PayloadAction<number>) => {
      state.productsData.pageIndex = action.payload;
    },
    setClientsPage: (state: ProductsStore, action: PayloadAction<number>) => {
      state.clients.pageIndex = action.payload;
    },
    setNewProductData: (
      state: ProductsStore,
      action: PayloadAction<{ key: string; value: any }>,
    ) => {
      const { key, value } = action.payload;
      state.newProductData = { ...state.newProductData, [key]: value };
    },
    setUpdatedProductData: (
      state: ProductsStore,
      action: PayloadAction<{ key: string; value: any }>,
    ) => {
      const { key, value } = action.payload;
      state.updatedProductData = { ...state.updatedProductData, [key]: value };
    },
    addingProduct: (state: ProductsStore) => {
      state.newProductData = initialState.newProductData;
      state.productRedemptionClients = [];
      state.isValidating = false;
    },
    updatingProduct: (state: ProductsStore) => {
      state.updatedProductData = initialState.updatedProductData;
      state.productRedemptionClients = [];
    },
    setProductDetails: (
      state: ProductsStore,
      action: PayloadAction<ProductModel>,
    ) => {
      state.updatedProductData = action.payload;
      state.productDetails = action.payload;
    },

    setClientViewDynamicSearchFilter: (
      state: ProductsStore,
      action: PayloadAction<EqualFilter[]>,
    ) => {
      state.clientFilter = { ...state.clientFilter, or: action.payload };
    },
    setRedemptionClients: (
      state: ProductsStore,
      action: PayloadAction<ProductRedemptionClients[]>,
    ) => {
      action.payload.forEach((client) => {
        if (!state.productRedemptionClients.includes(client)) {
          state.productRedemptionClients.push(client);
        }
      });
    },
    setUpdateProductRedemptionClients: (
      state: ProductsStore,
      action: PayloadAction<ProductRedemptionClients[]>,
    ) => {
      action.payload.forEach((client) => {
        if (!state.productRedemptionClients.includes(client)) {
          state.productRedemptionClients.push(client);
        }
      });
    },
    clearFilters: (state: ProductsStore) => {
      state.productFilter = initialState.productFilter;
      state.searchingValue = undefined;
      state.productItemsSearchValue = undefined;
      state.tabFilterValue = initialState.tabFilterValue;
    },
  },
});

export const {
  setProductsFetchingData,
  setEqualFilter,
  setProductViewDynamicSearchFilter,
  setSearchKey,
  setTabFilterChange,
  setProductsPage,
  setIsValidating,
  setNewProductData,
  addingProduct,
  setProductDetails,
  setClientsFetchingData,
  setClientViewDynamicSearchFilter,
  setClientSearchKey,
  setRedemptionClients,
  setClientsPage,
  setUpdatedClientsFetchingData,
  setUpdateProductRedemptionClients,
  setUpdatedProductData,
  updatingProduct,
  setProductItemsSearchKey,
  clearFilters,
} = slice.actions;
export const reducer = slice.reducer;

export const getProductsData = (): AsyncAppThunk => async (dispatch, store) => {
  dispatch(
    showLoaderWhile(async () => {
      var productsPaging = store().products.productsData;
      var queryOptions = getFilterQuery(
        store().products.productFilter,
        productsPaging.pageIndex * productsPaging.pageSize,
        productsPaging.pageSize,
      );
      const apiResponse = await getProductsService(queryOptions);
      if (
        apiResponse &&
        Object.keys(apiResponse).length > 0 &&
        apiResponse.result !== null
      ) {
        dispatch(setProductsFetchingData(apiResponse.result));
      }
    }),
  );
};
export const getProductsLookups = (): AsyncAppThunk => async (dispatch) => {
  dispatch(
    showLoaderWhile(async () => {
      const apiResponse = await getProductsService();
      if (
        apiResponse &&
        Object.keys(apiResponse).length > 0 &&
        apiResponse.result !== null
      ) {
        dispatch(setProductsFetchingData(apiResponse.result));
      }
    }),
  );
};

export const addProduct =
  (productRedemptionClients: ProductRedemptionClients[]): AsyncAppThunk =>
  async (dispatch, store) => {
    var productState = store().products;
    const newProduct = {
      ...productState.newProductData,
      productRedemptionClients: productRedemptionClients,
    } as ProductRequestModel;
    dispatch(setIsValidating(true));
    if (Object.keys(validateAddProductState(store().products)).length === 0) {
      dispatch(
        showLoaderWhile(async () => {
          dispatch(setIsValidating(false));
          var response = await addProductService(newProduct);
          if (
            response &&
            Object.keys(response).length > 0 &&
            response.result !== null
          ) {
            CustomizedBack();
            dispatch(addingProduct());
            dispatch(
              addSnack({
                message: translate(
                  "Product_Added_Successfully",
                  "Product Added Successfully",
                ),
              }),
            );
          }
        }),
      );
    }
  };
export const editProduct = (): AsyncAppThunk => async (dispatch, store) => {
  var productState = store().products;

  dispatch(setIsValidating(true));
  if (Object.keys(validateEditProductState(store().products)).length === 0) {
    dispatch(
      showLoaderWhile(async () => {
        dispatch(setIsValidating(false));
        var updatedProductData = {
          ...productState.updatedProductData,
          productRedemptionClients: productState.productRedemptionClients,
        } as UpdateProductRequestModel;
        var response = await editProductService(updatedProductData);
        if (
          response &&
          Object.keys(response).length > 0 &&
          response.result !== null
        ) {
          CustomizedBack();
          dispatch(updatingProduct());
          dispatch(
            addSnack({
              message: translate(
                "Product_Edited_Successfully",
                "Product Edited Successfully",
              ),
            }),
          );
        }
      }),
    );
  }
};

export const getProductDetailsById =
  (id: string): AsyncAppThunk =>
  async (dispatch) => {
    dispatch(
      showLoaderWhile(async () => {
        const apiResponse = await getProductDetailsByIdService(id);
        if (
          apiResponse &&
          Object.keys(apiResponse).length > 0 &&
          apiResponse.result !== null
        ) {
          dispatch(setProductDetails(apiResponse.result));
        }
      }),
    );
  };

export const getClients = (): AsyncAppThunk => async (dispatch, store) => {
  dispatch(
    showLoaderWhile(async () => {
      var clients = store().products.clients;
      var queryOptions = getFilterQuery(
        store().products.clientFilter,
        clients.pageIndex * clients.pageSize,
        clients.pageSize,
      );
      const apiResponse = await getClientService(queryOptions);
      if (
        apiResponse &&
        Object.keys(apiResponse).length > 0 &&
        apiResponse.result !== null
      ) {
        dispatch(setClientsFetchingData(apiResponse.result));
      }
    }),
  );
};
export const getUpdatedClients =
  (): AsyncAppThunk => async (dispatch, store) => {
    dispatch(
      showLoaderWhile(async () => {
        var clients = store().products.clients;
        var queryOptions = getFilterQuery(
          store().products.clientFilter,
          clients.pageIndex * clients.pageSize,
          clients.pageSize,
        );
        const apiResponse = await getClientService(queryOptions);
        if (
          apiResponse &&
          Object.keys(apiResponse).length > 0 &&
          apiResponse.result !== null
        ) {
          dispatch(setUpdatedClientsFetchingData(apiResponse.result));
        }
      }),
    );
  };
